import { ChangeEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ProgressiveUploadButton } from "ui/shared/controls/progressiveUploadButton/ProgressiveUploadButton";
import { ServiceMessageOverride, ServiceMessageType } from 'shared/models';
import { TextField } from "ui/shared/controls";

require('./ServiceMessageOverrideRow.css');

export type Props = {
    serviceMessageType: ServiceMessageType;
    displayName?: string;
    override: ServiceMessageOverride | null;
    readOnly?: boolean;
    urlFunc(file: File, serviceMessageType: ServiceMessageType): Promise<string>;
    onUploadDone(file: File, serviceMessageType: ServiceMessageType): void;
    onDelete(serviceMessageType: ServiceMessageType): void;
    onFocus(serviceMessageType: ServiceMessageType): void;
    onBlur(): void;
    onTextChanged(serviceMessageType: ServiceMessageType, text: string): void;
};


export const ServiceMessageOverrideRow = ({ serviceMessageType, displayName, onFocus, onBlur, onTextChanged, override, readOnly = false, urlFunc, onUploadDone, onDelete }: Props) => {
    const { t } = useTranslation();
    
    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        onTextChanged(serviceMessageType, e.currentTarget.value);
    }, [ serviceMessageType, onTextChanged ]);

    const hasFile = !!(override?.imageFileName || override?.audioFileName || override?.videoFileName);

    return (
        <form
            className="service-message-override-row ui form"
            onFocus={ () => onFocus(serviceMessageType) }
            onBlur={ onBlur }
            tabIndex={ 0 }
        >
            <span className="_type">
                { displayName || t(`SVCMSG.${serviceMessageType}`) }
            </span>
            <i className="ui right angle icon"/>
            { (hasFile || readOnly)
                ? <span className="_filename">
                    { override?.imageFileName || override?.audioFileName || override?.videoFileName || t('NONE') }
                </span>
                : <ProgressiveUploadButton
                    id={ `service-message-override-${serviceMessageType}-filepicker` }
                    className="_filename"
                    urlFunc={ async file => urlFunc(file, serviceMessageType) }
                    onUploadDone={ file => onUploadDone(file, serviceMessageType) }
                    accept="image/*"
                    size="tiny"
                />
            }
            { hasFile && !readOnly &&
                <button
                    type="button"
                    className="ui basic tiny icon button _delete-button"
                    onClick={ () => onDelete(serviceMessageType) }
                >
                    <i className="red remove icon"/>
                </button>
            }
            <TextField
                id="service-message-override-text"
                className="_text"
                name={ `properties.serviceMessageOverrides.${serviceMessageType}.text` }
                maxLength={ 64 }
                placeholder={ t('LABEL.CUSTOM_TEXT') }
                readOnly={ readOnly }
                value={ override?.text || '' }
                onChange={ handleChange }
            />
        </form>
    );
};
